import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Container as StyledContainer, Section } from '../../styles/GlobalStyles';
import NetworkBackground from '../Particles/NetworkBackground';

/* Mesmas medidas do hero das páginas de solução (ProductHero), para que a área
   da animação tenha a mesma altura em todo o site. */
const HeroContainer = styled(Section)`
  background: ${theme.colors.neutral.white};
  position: relative;
  overflow: hidden;
  padding-top: calc(80px + ${theme.spacing['3xl']});
  padding-bottom: ${theme.spacing['4xl']};

  @media (max-width: ${theme.breakpoints.md}) {
    padding-top: calc(70px + ${theme.spacing.xl});
    padding-bottom: ${theme.spacing.xl};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding-top: calc(70px + ${theme.spacing.lg});
    padding-bottom: ${theme.spacing.lg};
    overflow-x: hidden;
    overflow-y: visible;
    width: 100%;
    max-width: 100vw;
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing['4xl']};
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 2;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.xl};
    text-align: center;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    display: flex;
    flex-direction: column;
    gap: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
`;

const TextContent = styled.div`
  z-index: 2;

  @media (max-width: ${theme.breakpoints.sm}) {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 0 ${theme.spacing.md};
    gap: ${theme.spacing.xs};
  }
`;

const Title = styled.h1`
  margin-bottom: ${theme.spacing.lg};
  user-select: none;
  
  .highlight {
    color: ${theme.colors.primary.synapse};
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 3px;
      left: -2px;
      right: -2px;
      height: 8px;
      background: ${theme.colors.primary.accent};
      opacity: 0.8;
      border-radius: 12px;
      transform: rotate(-0.5deg) scaleY(0.6);
      z-index: -1;
    }
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin-bottom: 0;
    position: relative;
    z-index: 2;
    min-height: 220px;
    display: block;
    text-align: center;
    padding: ${theme.spacing.md};
    padding-top: ${theme.spacing['2xl']};
    line-height: 1.35;
    font-size: ${theme.typography.fontSize['3xl']};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.neutral.gray600};
  margin-bottom: ${theme.spacing.xl};
  line-height: ${theme.typography.lineHeight.relaxed};
  font-weight: ${theme.typography.fontWeight.medium};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.base};
    margin-bottom: 0;
    position: relative;
    z-index: 2;
  }
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.neutral.gray700};
  margin-bottom: ${theme.spacing['2xl']};
  line-height: ${theme.typography.lineHeight.relaxed};

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.typography.fontSize.sm};
    margin-bottom: ${theme.spacing.sm};
    position: relative;
    z-index: 2;
    margin-top: 0;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    text-align: center;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing['2xl']};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;
    margin-top: ${theme.spacing.lg};
  }
`;

const PrimaryButton = styled.button`
  background: ${theme.colors.primary.synapse};
  color: ${theme.colors.neutral.white};
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  border-radius: ${theme.borderRadius.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  transition: all ${theme.transitions.normal};

  &:hover {
    background: ${theme.colors.primary.accent};
    transform: translateY(-3px);
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const VisualContent = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.lg}) {
    min-height: 250px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 220px;
    min-height: 220px;
    pointer-events: none;
  }
`;

const HeroSection: React.FC = () => {
  const scrollToSolutions = () => {
    const element = document.getElementById('solutions');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer id="hero">
      <NetworkBackground />

      <StyledContainer>
        <HeroContent>
          <TextContent>
            <Title>
              Conecte <span className="highlight">dados</span><br />a <span className="highlight">decisões</span> estratégicas
            </Title>
            
            <Subtitle>
              Agentes inteligentes, modelos de IA sob medida e engenharia de dados
            </Subtitle>
            
            <Description>
              Desenvolvemos soluções de IA e automação inteligente com foco em resultados mensuráveis, 
              governança de dados e conformidade com LGPD. Do conceito à implementação, transformamos 
              dados em vantagem competitiva real.
            </Description>

            <CTAGroup>
              <PrimaryButton onClick={scrollToSolutions}>
                Conheça Nossas Soluções
                →
              </PrimaryButton>
            </CTAGroup>
          </TextContent>

          <VisualContent>
            {/* Área visual limpa sem elementos extras */}
          </VisualContent>
        </HeroContent>
      </StyledContainer>
    </HeroContainer>
  );
};

export default HeroSection; 